// src/components/qr-customer/FridgeProducts.js
// ✅ Auth gate: must login before seeing products
// ✅ Fully responsive mobile header
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scanFridge, resolveImageUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import {
  ArrowLeftIcon, MapPinIcon, CubeIcon,
  PlusIcon, MinusIcon, TrashIcon, ArrowRightIcon,
  ShoppingBagIcon, XMarkIcon,
  UserIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';

const CATEGORY_EMOJI = { Milk:'🥛', Curd:'🥛', Paneer:'🧀', Ghee:'🫙', Butter:'🧈', Buttermilk:'🧃' };
const getEmoji = (p) => CATEGORY_EMOJI[p?.category] || '🥛';
const getImg   = (p) => {
  const raw = p?.image_url || p?.imageUrl || p?.image || null;
  return resolveImageUrl ? resolveImageUrl(raw) : raw;
};

export default function FridgeProducts() {
  const { fridgeId } = useParams();
  const navigate     = useNavigate();
  const { clearCart, addToCart }          = useCart();
  const { customer, isLoggedIn, logout, authLoading } = useCustomerAuth();

  const [fridge,       setFridge]       = useState(null);
  const [products,     setProducts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [imgErrors,    setImgErrors]    = useState({});
  const [sel,          setSel]          = useState({});
  const [step,         setStep]         = useState('browse');
  const [dispensing,   setDispensing]   = useState(null);
  const [showAuth,     setShowAuth]     = useState(false);
  const [showProfile,  setShowProfile]  = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // ── Load fridge data ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await scanFridge(fridgeId);
        if (r.data.success) {
          setFridge(r.data.fridge);
          setProducts(r.data.products || []);
        } else {
          toast.error(r.data.message || 'Fridge not found');
          navigate('/scan');
        }
      } catch {
        toast.error('Failed to load fridge');
        navigate('/scan');
      } finally {
        setLoading(false);
      }
    })();
  }, [fridgeId]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const fn = () => setShowDropdown(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showDropdown]);

  // ── Show auth modal automatically if not logged in ────────────────────────
  useEffect(() => {
    if (!authLoading && !isLoggedIn) {
      setShowAuth(true);
    }
  }, [authLoading, isLoggedIn]);

  const changeQty = (product, delta) => {
    const max = Number(product.quantity) || 0;
    if (max === 0) return;
    setSel(prev => {
      const cur  = prev[product.id] || 0;
      const next = Math.max(0, Math.min(max, cur + delta));
      if (next === cur) return prev;
      if (delta > 0 && cur === 0) setDispensing(product.id);
      if (next === 0) { const { [product.id]: _, ...rest } = prev; return rest; }
      return { ...prev, [product.id]: next };
    });
    setTimeout(() => setDispensing(null), 600);
  };

  const clearSelection = () => { setSel({}); toast('Selection cleared', { icon: '🗑️' }); };

  const selectedItems = products.filter(p => (sel[p.id] || 0) > 0);
  const totalQty      = Object.values(sel).reduce((a, b) => a + b, 0);
  const totalAmt      = selectedItems.reduce((a, p) => a + Number(p.price) * sel[p.id], 0);

  const handleConfirm = () => {
    if (!isLoggedIn) { setShowAuth(true); return; }
    if (!totalQty) { toast.error('Select at least one item'); return; }
    setStep('confirm');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckout = () => {
    if (!isLoggedIn) { setShowAuth(true); return; }
    clearCart();
    selectedItems.forEach(p => {
      for (let i = 0; i < sel[p.id]; i++) {
        addToCart({ ...p, fridgeId, product_id: p.product_id, row_id: p.id });
      }
    });
    navigate('/fridge-checkout', {
      state: { fridgeId, fridgeName: fridge?.location, apartment: fridge?.apartment },
    });
  };

  const handleLogout = () => { logout(); setShowDropdown(false); toast.success('Logged out 👋'); };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    html{-webkit-text-size-adjust:100%;}
    body{font-family:'Nunito',sans-serif;background:#f9fafb;color:#111827;overflow-x:hidden;min-height:100vh;}
    ::-webkit-scrollbar{width:4px;}
    ::-webkit-scrollbar-thumb{background:#a7f3d0;border-radius:99px;}

    /* ─── HEADER ─── */
    .hdr{position:sticky;top:0;z-index:60;background:rgba(255,255,255,.97);backdrop-filter:blur(14px);border-bottom:1px solid rgba(5,150,105,.12);box-shadow:0 2px 12px rgba(0,0,0,.05);}
    .hdr-in{max-width:1200px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;padding:0 .875rem;height:54px;gap:.5rem;}
    @media(min-width:640px){.hdr-in{padding:0 1.5rem;height:62px;}}

    /* logo */
    .hdr-logo{display:flex;align-items:center;cursor:pointer;flex-shrink:0;}
    .hdr-logo img{height:32px;object-fit:contain;}
    @media(min-width:480px){.hdr-logo img{height:38px;}}
    @media(min-width:768px){.hdr-logo img{height:44px;}}

    /* right side */
    .hdr-r{display:flex;align-items:center;gap:.35rem;flex-shrink:0;}
    @media(min-width:480px){.hdr-r{gap:.5rem;}}

    /* back button */
    .hdr-back{display:flex;align-items:center;gap:.25rem;background:#f9fafb;color:#6b7280;border:1.5px solid #e5e7eb;border-radius:9999px;padding:.3rem .65rem;font-weight:700;font-size:.7rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s;white-space:nowrap;flex-shrink:0;}
    .hdr-back svg{width:.65rem;height:.65rem;}
    .hdr-back:hover{border-color:#a7f3d0;color:#059669;background:#f0fdf4;}
    @media(min-width:480px){.hdr-back{font-size:.75rem;padding:.32rem .8rem;}}

    /* step pill — hidden on very small screens */
    .hdr-steps{display:none;}
    @media(min-width:400px){.hdr-steps{display:flex;align-items:center;gap:.35rem;background:#f0fdf4;border:1.5px solid #d1fae5;border-radius:9999px;padding:.28rem .75rem;font-size:.65rem;font-weight:700;color:#9ca3af;flex-shrink:0;}}
    @media(min-width:640px){.hdr-steps{font-size:.72rem;padding:.3rem .9rem;gap:.45rem;}}
    .hdr-steps span.act{color:#059669;}
    .hdr-steps .div{color:#d1fae5;}

    /* login button */
    .hdr-lbtn{display:flex;align-items:center;gap:.25rem;background:white;color:#059669;border:1.5px solid #d1fae5;font-weight:700;font-size:.7rem;padding:.3rem .65rem;border-radius:9999px;cursor:pointer;transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;flex-shrink:0;}
    .hdr-lbtn:hover{background:#f0fdf4;}
    .hdr-lbtn svg{width:.72rem;height:.72rem;}
    @media(min-width:480px){.hdr-lbtn{font-size:.75rem;padding:.32rem .8rem;}}

    /* profile button */
    .hdr-pw{position:relative;display:inline-flex;flex-shrink:0;}
    .hdr-pb{display:flex;align-items:center;gap:.25rem;background:#ecfdf5;border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.7rem;padding:.25rem .55rem .25rem .25rem;border-radius:9999px;cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .hdr-pb:hover{background:#d1fae5;}
    .hdr-av{width:1.4rem;height:1.4rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.58rem;font-weight:800;flex-shrink:0;}
    @media(min-width:380px){.hdr-av{width:1.55rem;height:1.55rem;font-size:.62rem;}}
    .hdr-pnm{display:none;}
    @media(min-width:500px){.hdr-pnm{display:block;max-width:60px;overflow:hidden;text-overflow:ellipsis;font-size:.72rem;}}

    /* dropdown */
    .hdr-dd{position:absolute;top:calc(100% + 8px);right:0;background:white;border-radius:1.1rem;padding:.35rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;min-width:195px;z-index:200;animation:ddIn .18s ease-out;}
    @keyframes ddIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
    .dd-hd{padding:.65rem .9rem;border-bottom:1px solid #f3f4f6;margin-bottom:.2rem;}
    .dd-av{width:2.25rem;height:2.25rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.88rem;font-weight:800;margin-bottom:.35rem;}
    .dd-nm{font-weight:800;font-size:.88rem;color:#111827;}
    .dd-ph{font-size:.7rem;color:#6b7280;margin-top:.08rem;}
    .dd-it{display:flex;align-items:center;gap:.5rem;padding:.5rem .85rem;border-radius:.6rem;font-size:.82rem;font-weight:600;color:#374151;cursor:pointer;transition:background .15s;border:none;background:none;width:100%;font-family:'Nunito',sans-serif;}
    .dd-it:hover{background:#f0fdf4;color:#059669;}
    .dd-it.dng:hover{background:#fff1f2;color:#ef4444;}
    .dd-it svg{width:.88rem;height:.88rem;flex-shrink:0;}

    /* ─── AUTH GATE (not logged in) ─── */
    .gate{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem 1rem;background:linear-gradient(150deg,#f0fdf4,#ffffff 60%,#f0f9ff);text-align:center;}
    .gate-ico{font-size:3.5rem;margin-bottom:1rem;}
    .gate-t{font-size:1.3rem;font-weight:900;color:#111827;margin-bottom:.5rem;}
    @media(min-width:480px){.gate-t{font-size:1.55rem;}}
    .gate-s{font-size:.88rem;color:#6b7280;line-height:1.65;margin-bottom:1.75rem;max-width:320px;}
    .gate-btn{display:inline-flex;align-items:center;gap:.45rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:800;font-size:.95rem;padding:.75rem 1.75rem;border-radius:9999px;border:none;cursor:pointer;font-family:'Nunito',sans-serif;box-shadow:0 6px 18px rgba(16,185,129,.3);transition:all .3s;}
    .gate-btn:hover{transform:translateY(-2px);box-shadow:0 10px 24px rgba(16,185,129,.38);}
    .gate-btn svg{width:1rem;height:1rem;}

    /* ─── LOCATION BANNER ─── */
    .loc-bar{background:linear-gradient(135deg,#f0fdf4,#ecfeff);border-bottom:1px solid #d1fae5;padding:.6rem .875rem;}
    .loc-in{max-width:1200px;margin:0 auto;display:flex;align-items:center;gap:.45rem;}
    .loc-in svg{width:.82rem;height:.82rem;color:#059669;flex-shrink:0;}
    .loc-t{font-size:.78rem;font-weight:600;color:#374151;}
    .loc-t strong{color:#065f46;}

    /* ─── MAIN ─── */
    .pg-main{max-width:1200px;margin:0 auto;padding:1.25rem .875rem 10rem;}
    @media(min-width:640px){.pg-main{padding:1.75rem 1.5rem 10rem;}}
    .sec-lbl{font-size:.62rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#059669;margin-bottom:1rem;display:flex;align-items:center;gap:.5rem;}
    .sec-lbl::after{content:'';flex:1;height:1px;background:linear-gradient(to right,#d1fae5,transparent);}

    /* ─── PRODUCT GRID ─── */
    .p-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.75rem;}
    @media(min-width:420px){.p-grid{gap:1rem;}}
    @media(min-width:560px){.p-grid{grid-template-columns:repeat(3,1fr);}}
    @media(min-width:768px){.p-grid{grid-template-columns:repeat(4,1fr);gap:1.25rem;}}
    @media(min-width:1024px){.p-grid{grid-template-columns:repeat(5,1fr);}}

    /* ─── PRODUCT CELL ─── */
    .p-cell{background:#fff;position:relative;display:flex;flex-direction:column;align-items:center;padding:.9rem .7rem .8rem;text-align:center;border:1px solid #e5e7eb;border-radius:.9rem;transition:background .18s;min-height:185px;}
    @media(min-width:480px){.p-cell{padding:1.1rem .85rem .9rem;border-radius:1rem;min-height:200px;}}
    .p-cell:hover{background:#fafffe;}
    .p-cell.selected{background:#f0fdf4;border-color:#a7f3d0;}
    .p-cell.out{opacity:.45;pointer-events:none;}
    @keyframes dispense{0%{background:#dcfce7}60%{background:#dcfce7}100%{background:#f0fdf4}}
    .p-cell.dispensing{animation:dispense .55s ease forwards;}
    .p-slot{position:absolute;top:.4rem;left:.5rem;font-size:.5rem;font-weight:800;color:#d1d5db;}
    .p-sel-badge{position:absolute;top:.35rem;right:.35rem;width:1.2rem;height:1.2rem;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);color:white;font-size:.55rem;font-weight:900;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(5,150,105,.35);}
    .p-out-badge{position:absolute;top:.35rem;right:.35rem;font-size:.48rem;font-weight:700;color:#ef4444;background:#fee2e2;border:1px solid #fecaca;padding:.08rem .32rem;border-radius:9999px;}
    .p-img{width:60px;height:60px;margin-bottom:.5rem;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    @media(min-width:480px){.p-img{width:72px;height:72px;}}
    .p-img img{width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 3px 8px rgba(0,0,0,.1));transition:transform .3s;}
    .p-cell:hover .p-img img{transform:scale(1.06);}
    .p-emoji{font-size:2.6rem;line-height:1;}
    @media(min-width:480px){.p-emoji{font-size:3rem;}}
    .p-name{font-size:.72rem;font-weight:700;color:#059669;margin-bottom:.14rem;width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:480px){.p-name{font-size:.76rem;}}
    .p-price{font-size:.85rem;font-weight:900;color:#111827;margin-bottom:.22rem;}
    @media(min-width:480px){.p-price{font-size:.9rem;}}
    .p-stock{display:flex;align-items:center;justify-content:center;gap:.2rem;font-size:.55rem;color:#9ca3af;margin-bottom:.5rem;}
    @media(min-width:480px){.p-stock{font-size:.6rem;}}
    .p-dot{width:.28rem;height:.28rem;border-radius:50%;flex-shrink:0;}
    .p-dot.in{background:#10b981;} .p-dot.low{background:#f59e0b;} .p-dot.out{background:#ef4444;}
    .p-qty{display:flex;align-items:center;background:#f0fdf4;border:1.5px solid #d1fae5;border-radius:9999px;padding:.12rem;width:100%;margin-top:auto;}
    .p-qbtn{width:1.45rem;height:1.45rem;border-radius:50%;border:none;background:transparent;cursor:pointer;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:#9ca3af;transition:all .15s;}
    .p-qbtn svg{width:.65rem;height:.65rem;}
    .p-qbtn.plus{color:#059669;}
    .p-qbtn.plus:hover{background:#d1fae5;color:#065f46;}
    .p-qbtn.minus:hover:not(:disabled){background:#fee2e2;color:#dc2626;}
    .p-qbtn:disabled{opacity:.3;cursor:not-allowed;}
    .p-qval{flex:1;text-align:center;font-size:.85rem;font-weight:800;color:#111827;line-height:1;}
    .p-qval.zero{color:#d1d5db;} .p-qval.pos{color:#059669;}
    .p-empty{grid-column:1/-1;text-align:center;padding:3rem 1rem;}
    .p-empty svg{width:2.75rem;height:2.75rem;color:#e5e7eb;margin:0 auto .75rem;display:block;}
    .p-empty-t{font-size:.9rem;font-weight:700;color:#9ca3af;}

    /* ─── TRAY ─── */
    .tray{position:fixed;bottom:0;left:0;right:0;z-index:60;background:rgba(255,255,255,.97);backdrop-filter:blur(16px);border-top:1.5px solid #d1fae5;box-shadow:0 -4px 20px rgba(5,150,105,.08);}
    .tray-in{max-width:1200px;margin:0 auto;padding:.75rem .875rem;display:flex;align-items:center;justify-content:space-between;gap:.75rem;}
    @media(min-width:640px){.tray-in{padding:.85rem 1.5rem;}}
    .tray-l{display:flex;align-items:center;gap:.6rem;min-width:0;flex:1;}
    .tray-ico{width:34px;height:34px;border-radius:.6rem;flex-shrink:0;background:#f0fdf4;border:1.5px solid #d1fae5;display:flex;align-items:center;justify-content:center;}
    .tray-ico svg{width:.88rem;height:.88rem;color:#059669;}
    @media(min-width:480px){.tray-ico{width:38px;height:38px;} .tray-ico svg{width:.95rem;height:.95rem;}}
    .tray-cnt{font-size:.68rem;color:#6b7280;font-weight:600;}
    @media(min-width:480px){.tray-cnt{font-size:.72rem;}}
    .tray-amt{font-size:1rem;font-weight:900;background:linear-gradient(135deg,#059669,#0891b2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
    @media(min-width:480px){.tray-amt{font-size:1.1rem;}}
    .tray-acts{display:flex;align-items:center;gap:.4rem;flex-shrink:0;}
    .tray-clr{padding:.38rem .65rem;border-radius:9999px;border:1.5px solid #e5e7eb;background:transparent;color:#9ca3af;font-size:.68rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s;display:flex;align-items:center;gap:.25rem;white-space:nowrap;}
    .tray-clr svg{width:.65rem;height:.65rem;}
    .tray-clr:hover{border-color:#fca5a5;color:#ef4444;background:#fff5f5;}
    @media(min-width:480px){.tray-clr{font-size:.72rem;padding:.42rem .75rem;}}
    .tray-cfm{padding:.45rem 1rem;border-radius:9999px;border:none;background:linear-gradient(135deg,#10b981,#059669);color:white;font-size:.8rem;font-weight:800;cursor:pointer;font-family:'Nunito',sans-serif;display:flex;align-items:center;gap:.3rem;white-space:nowrap;box-shadow:0 4px 12px rgba(16,185,129,.3);transition:all .25s;}
    .tray-cfm svg{width:.75rem;height:.75rem;}
    .tray-cfm:hover{transform:translateY(-1px);}
    .tray-cfm:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none;}
    @media(min-width:480px){.tray-cfm{font-size:.85rem;padding:.5rem 1.3rem;}}
    .tray-empty{text-align:center;padding:.5rem;font-size:.75rem;color:#9ca3af;font-weight:600;flex:1;}
    .tray-empty strong{color:#059669;}

    /* ─── CONFIRM STEP ─── */
    .cfm-wrap{max-width:520px;margin:0 auto;padding:1.25rem .875rem 8rem;}
    @media(min-width:640px){.cfm-wrap{padding:2rem 1.5rem 8rem;}}
    .cfm-hd{text-align:center;margin-bottom:1.5rem;}
    .cfm-ico{width:52px;height:52px;border-radius:.9rem;margin:0 auto .75rem;background:linear-gradient(135deg,#ecfdf5,#e0f2fe);border:1.5px solid #a7f3d0;display:flex;align-items:center;justify-content:center;}
    .cfm-ico svg{width:1.4rem;height:1.4rem;color:#059669;}
    .cfm-t{font-size:1.25rem;font-weight:900;color:#111827;margin-bottom:.25rem;}
    .cfm-s{font-size:.8rem;color:#6b7280;}
    .cfm-list{background:white;border:1.5px solid #f3f4f6;border-radius:1rem;overflow:hidden;margin-bottom:1.1rem;box-shadow:0 2px 8px rgba(0,0,0,.03);}
    .cfm-it{display:flex;align-items:center;gap:.8rem;padding:.8rem 1rem;border-bottom:1px solid #f3f4f6;}
    .cfm-it:last-child{border-bottom:none;}
    .cfm-it-img{width:42px;height:42px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:#f9fafb;border-radius:.55rem;overflow:hidden;border:1px solid #f3f4f6;}
    .cfm-it-img img{width:100%;height:100%;object-fit:contain;}
    .cfm-it-img span{font-size:1.45rem;}
    .cfm-it-info{flex:1;min-width:0;}
    .cfm-it-nm{font-size:.85rem;font-weight:700;color:#111827;margin-bottom:.08rem;}
    .cfm-it-u{font-size:.7rem;color:#9ca3af;}
    .cfm-it-adj{display:flex;align-items:center;gap:.25rem;background:#f0fdf4;border:1.5px solid #d1fae5;border-radius:9999px;padding:.1rem .18rem;}
    .cfm-adj-btn{width:1.35rem;height:1.35rem;border-radius:50%;border:none;background:transparent;cursor:pointer;color:#9ca3af;display:flex;align-items:center;justify-content:center;transition:all .15s;}
    .cfm-adj-btn svg{width:.62rem;height:.62rem;}
    .cfm-adj-btn:hover{color:#059669;background:#d1fae5;}
    .cfm-adj-val{font-size:.8rem;font-weight:800;color:#111827;min-width:1.1rem;text-align:center;}
    .cfm-it-sub{font-size:.9rem;font-weight:900;color:#059669;margin-top:.2rem;text-align:right;}
    .cfm-totals{background:white;border:1.5px solid #f3f4f6;border-radius:1rem;padding:.9rem 1rem;margin-bottom:1.25rem;box-shadow:0 2px 8px rgba(0,0,0,.03);}
    .cfm-tr{display:flex;justify-content:space-between;align-items:center;margin-bottom:.45rem;}
    .cfm-tr:last-child{margin-bottom:0;}
    .cfm-tr-l{font-size:.8rem;color:#6b7280;font-weight:600;}
    .cfm-tr-v{font-size:.85rem;font-weight:700;color:#374151;}
    .cfm-tr.big .cfm-tr-l{font-size:.88rem;color:#374151;font-weight:700;}
    .cfm-tr.big .cfm-tr-v{font-size:1.2rem;font-weight:900;background:linear-gradient(135deg,#059669,#0891b2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
    .cfm-div{height:1px;background:#f3f4f6;margin:.5rem 0;}
    .cfm-back-lnk{display:inline-flex;align-items:center;gap:.3rem;font-size:.78rem;color:#9ca3af;cursor:pointer;font-weight:600;background:none;border:none;font-family:'Nunito',sans-serif;transition:color .2s;margin-bottom:1.25rem;}
    .cfm-back-lnk svg{width:.68rem;height:.68rem;}
    .cfm-back-lnk:hover{color:#374151;}
    .cfm-pay{width:100%;padding:.85rem;border-radius:9999px;border:none;background:linear-gradient(135deg,#10b981,#059669);color:white;font-family:'Nunito',sans-serif;font-size:.95rem;font-weight:800;cursor:pointer;transition:all .3s;display:flex;align-items:center;justify-content:center;gap:.45rem;box-shadow:0 6px 16px rgba(16,185,129,.3);}
    .cfm-pay svg{width:.95rem;height:.95rem;}
    .cfm-pay:hover{transform:translateY(-2px);box-shadow:0 10px 24px rgba(16,185,129,.38);}

    /* ─── LOADER ─── */
    .vm-loading{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f9fafb;}
    .vm-spinner{width:2.75rem;height:2.75rem;border-radius:50%;border:3px solid #e5e7eb;border-top-color:#059669;animation:vm-spin .8s linear infinite;}
    @keyframes vm-spin{to{transform:rotate(360deg);}}
  `;

  if (loading || authLoading) return (
    <><style>{css}</style><div className="vm-loading"><div className="vm-spinner"/></div></>
  );

  // ── AUTH GATE ─────────────────────────────────────────────────────────────
  // Always render the auth modal when needed; show gate UI behind it
  const AuthGate = () => (
    <>
      <header className="hdr">
        <div className="hdr-in">
          <div className="hdr-logo" onClick={() => navigate('/')}>
            <img src="/images/logo.png" alt="Fresh Hills"
              onError={e=>{e.target.onerror=null;e.target.style.display='none';}}/>
          </div>
          <div className="hdr-r">
            <button className="hdr-back" onClick={() => navigate('/scan')}>
              <ArrowLeftIcon/> Scan
            </button>
          </div>
        </div>
      </header>
      <div className="gate">
        <div className="gate-ico">🔐</div>
        <div className="gate-t">Login to Shop</div>
        <p className="gate-s">
          Please login with your WhatsApp number to browse and buy products from this fridge.
        </p>
        <button className="gate-btn" onClick={() => setShowAuth(true)}>
          <UserIcon/> Login via WhatsApp OTP
        </button>
      </div>
    </>
  );

  // ── Shared Header component ───────────────────────────────────────────────
  const Header = ({ stepLabel }) => (
    <header className="hdr">
      <div className="hdr-in">
        <div className="hdr-logo" onClick={() => navigate('/')}>
          <img src="/images/logo.png" alt="Fresh Hills"
            onError={e=>{e.target.onerror=null;e.target.style.display='none';}}/>
        </div>

        {/* Step pill — only when logged in */}
        {isLoggedIn && (
          <div className="hdr-steps">
            <span className={stepLabel==='browse'?'act':''}>Select</span>
            <span className="div">›</span>
            <span className={stepLabel==='confirm'?'act':''}>Confirm</span>
            <span className="div">›</span>
            <span>Pay</span>
          </div>
        )}

        <div className="hdr-r">
          {isLoggedIn ? (
            <div className="hdr-pw" onClick={e=>{e.stopPropagation();setShowDropdown(p=>!p);}}>
              <button className="hdr-pb">
                <div className="hdr-av">{(customer?.name||'U').charAt(0).toUpperCase()}</div>
                <span className="hdr-pnm">{(customer?.name||customer?.phone||'').split(' ')[0]}</span>
              </button>
              {showDropdown && (
                <div className="hdr-dd">
                  <div className="dd-hd">
                    <div className="dd-av">{(customer?.name||'U').charAt(0).toUpperCase()}</div>
                    <div className="dd-nm">{customer?.name||'Customer'}</div>
                    <div className="dd-ph">📱 +91 {customer?.phone}</div>
                  </div>
                  <button className="dd-it" onClick={()=>{setShowDropdown(false);setShowProfile(true);}}>
                    <ShoppingBagIcon/>My Orders
                  </button>
                  <button className="dd-it" onClick={()=>{setShowDropdown(false);setShowProfile(true);}}>
                    <UserCircleIcon/>My Profile
                  </button>
                  <button className="dd-it dng" onClick={handleLogout}>
                    <ArrowRightOnRectangleIcon/>Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="hdr-lbtn" onClick={()=>setShowAuth(true)}>
              <UserIcon/>Login
            </button>
          )}
          <button className="hdr-back"
            onClick={() => step==='confirm' ? setStep('browse') : navigate('/scan')}>
            <ArrowLeftIcon/>{step==='confirm'?'Back':'Scan'}
          </button>
        </div>
      </div>
    </header>
  );

  return (
    <>
      <style>{css}</style>
      {showAuth    && <WhatsAppAuthModal onSuccess={()=>setShowAuth(false)} onClose={()=>setShowAuth(false)}/>}
      {showProfile && <CustomerProfile onClose={()=>setShowProfile(false)}/>}

      {/* Show gate if not logged in */}
      {!isLoggedIn && <AuthGate/>}

      {/* Show products only when logged in */}
      {isLoggedIn && (
        <>
          {/* ── CONFIRM STEP ── */}
          {step === 'confirm' && (
            <>
              <Header stepLabel="confirm"/>
              <div className="cfm-wrap">
                <button className="cfm-back-lnk" onClick={() => setStep('browse')}>
                  <ArrowLeftIcon/> Edit selection
                </button>
                <div className="cfm-hd">
                  <div className="cfm-ico"><ShoppingBagIcon/></div>
                  <div className="cfm-t">Your Order</div>
                  <div className="cfm-s">{fridge?.apartment} · {fridge?.location}</div>
                </div>
                <div className="cfm-list">
                  {selectedItems.map(p => {
                    const img  = getImg(p);
                    const noImg= !img || imgErrors[p.id];
                    const qty  = sel[p.id];
                    const max  = Number(p.quantity) || 0;
                    return (
                      <div key={p.id} className="cfm-it">
                        <div className="cfm-it-img">
                          {noImg ? <span>{getEmoji(p)}</span>
                            : <img src={img} alt={p.name}
                                onError={() => setImgErrors(e=>({...e,[p.id]:true}))}/>}
                        </div>
                        <div className="cfm-it-info">
                          <div className="cfm-it-nm">{p.name}</div>
                          <div className="cfm-it-u">₹{Number(p.price).toFixed(2)} each</div>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'.25rem'}}>
                          <div className="cfm-it-adj">
                            <button className="cfm-adj-btn" onClick={() => changeQty(p, -1)}>
                              {qty === 1 ? <TrashIcon/> : <MinusIcon/>}
                            </button>
                            <span className="cfm-adj-val">{qty}</span>
                            <button className="cfm-adj-btn" onClick={() => changeQty(p, +1)}
                              disabled={qty >= max}><PlusIcon/></button>
                          </div>
                          <div className="cfm-it-sub">₹{(Number(p.price)*qty).toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="cfm-totals">
                  <div className="cfm-tr">
                    <span className="cfm-tr-l">Items ({totalQty})</span>
                    <span className="cfm-tr-v">₹{totalAmt.toFixed(2)}</span>
                  </div>
                  <div className="cfm-div"/>
                  <div className="cfm-tr big">
                    <span className="cfm-tr-l">Total</span>
                    <span className="cfm-tr-v">₹{totalAmt.toFixed(2)}</span>
                  </div>
                </div>
                <button className="cfm-pay" onClick={handleCheckout}>
                  <ArrowRightIcon/> Proceed to Payment
                </button>
              </div>
            </>
          )}

          {/* ── BROWSE STEP ── */}
          {step === 'browse' && (
            <>
              <Header stepLabel="browse"/>
              {fridge && (
                <div className="loc-bar">
                  <div className="loc-in">
                    <MapPinIcon/>
                    <span className="loc-t"><strong>{fridge.apartment}</strong> — {fridge.location}</span>
                  </div>
                </div>
              )}
              <div className="pg-main">
                <div className="sec-lbl">Available Products</div>
                <div className="p-grid">
                  {products.length === 0 ? (
                    <div className="p-empty">
                      <CubeIcon/><div className="p-empty-t">No products available</div>
                    </div>
                  ) : products.map((p, idx) => {
                    const out        = Number(p.quantity) === 0;
                    const max        = Number(p.quantity) || 0;
                    const isLow      = max > 0 && max <= 5;
                    const qty        = sel[p.id] || 0;
                    const isSelected = qty > 0;
                    const isDisp     = dispensing === p.id;
                    const img        = getImg(p);
                    const noImg      = !img || imgErrors[p.id];
                    const slotLabel  = String.fromCharCode(65 + Math.floor(idx/5)) + ((idx%5)+1);
                    return (
                      <div key={p.id}
                        className={`p-cell${out?' out':''}${isSelected?' selected':''}${isDisp?' dispensing':''}`}>
                        <span className="p-slot">{slotLabel}</span>
                        {isSelected && <span className="p-sel-badge">{qty}</span>}
                        {out && <span className="p-out-badge">Empty</span>}
                        <div className="p-img">
                          {noImg
                            ? <div className="p-emoji">{getEmoji(p)}</div>
                            : <img src={img} alt={p.name} loading="lazy"
                                onError={() => setImgErrors(e=>({...e,[p.id]:true}))}/>}
                        </div>
                        <div className="p-name" title={p.name}>{p.name}</div>
                        <div className="p-price">₹{Number(p.price).toFixed(2)}</div>
                        <div className="p-stock">
                          <span className={`p-dot ${out?'out':isLow?'low':'in'}`}/>
                          <span>{out?'Out of stock':isLow?`Only ${max} left`:`${max} in stock`}</span>
                        </div>
                        <div className="p-qty">
                          <button className="p-qbtn minus" disabled={qty===0||out} onClick={()=>changeQty(p,-1)}>
                            <MinusIcon/>
                          </button>
                          <span className={`p-qval${qty===0?' zero':' pos'}`}>{qty}</span>
                          <button className="p-qbtn plus" disabled={qty>=max||out} onClick={()=>changeQty(p,1)}>
                            <PlusIcon/>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* TRAY */}
              <div className="tray">
                <div className="tray-in">
                  {totalQty === 0 ? (
                    <div className="tray-empty">Tap <strong>+</strong> on any item to add it</div>
                  ) : (
                    <div className="tray-l">
                      <div className="tray-ico"><ShoppingBagIcon/></div>
                      <div>
                        <div className="tray-cnt">{totalQty} item{totalQty!==1?'s':''} selected</div>
                        <div className="tray-amt">₹{totalAmt.toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                  <div className="tray-acts">
                    {totalQty > 0 && (
                      <button className="tray-clr" onClick={clearSelection}>
                        <XMarkIcon/> Clear
                      </button>
                    )}
                    <button className="tray-cfm" disabled={totalQty===0} onClick={handleConfirm}>
                      Confirm <ArrowRightIcon/>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}